// eslint-disable-next-line @typescript-eslint/ban-ts-comment
import { TWEEN } from 'three/examples/jsm/libs/tween.module.min';
import { LoadingState } from '@/_models/virtualStaging/VSObject';
import { createHTMLMesh } from '@/_models/virtualStaging/HTMLMesh/HTMLMesh';
import { InteractiveGroup } from '@/_models/virtualStaging/HTMLMesh/utils/InteractiveGroup';
import { getXZCoordinatesOnCircle } from '@/components/util/math/getXZCoordinatesOnCircle';
import { AbstractHTMLMesh } from '@/_models/virtualStaging/HTMLMesh/component/AbstractHTMLMesh';
import { Inputs } from '@/_models/virtualStaging/HTMLMesh/component/PathFinder/types';

export class PathFinderMeshComponent extends AbstractHTMLMesh {
  public mesh;

  public hostPose;

  private onControllerButtonSecondaryClicked;

  inputs: Inputs = {
    html: null,
    visible: false,
    scale: 1,
    refs: null,
    hasPathFinderData: false,
  };

  onInit() {
    const { html } = this.inputs;
    this.initPathFinderMesh(html);
    this.initRoot();
    this.addMeshPointerEvents();
    this.getXRController();
    this.outputs.loadingState = LoadingState.Loaded;
  }

  initPathFinderMesh(pathFinderHTML) {
    this.mesh = createHTMLMesh(pathFinderHTML, this.context.three);
    this.mesh.material.side = this.context.three.DoubleSide;
    this.mesh.material.depthWrite = false;
    this.mesh.material.depthTest = false;
    this.mesh.renderOrder = 970;
    this.mesh.scale.setScalar(2);
    this.mesh.userData = { isPointerOver: false };
  }

  addMeshPointerEvents() {
    const group = new InteractiveGroup(this.context.renderer, this.context.camera, this.context);
    this.pivot.add(group);
    group.add(this.mesh);
  }

  setHostPose(pose) {
    this.hostPose = pose;
  }

  setPositionFromPose(pose) {
    if (!pose.rotation || !pose.position) return;
    const { x, z } = getXZCoordinatesOnCircle(0.85, pose.rotation.y + 180);
    this.root.position.set(pose.position.x + x, pose.position.y - 0.2, pose.position.z + z);
    this.root.lookAt(pose.position.x, pose.position.y - 0.1, pose.position.z);
  }

  setFromHostPose() {
    this.setPositionFromPose(this.hostPose);
  }

  setOnControllerButtonSecondaryClicked(cb) {
    this.onControllerButtonSecondaryClicked = cb;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  cycleVR(_session) {
    TWEEN.update();
    if (this.gamepadR?.buttons[4]?.pressed && !this.gamepadButtonActive && this.hostPose && this.inputs.hasPathFinderData) {
      this.setVisible(!this.inputs.visible);
      this.setFromHostPose();
      this.gamepadButtonActive = true;
    }
    if (!this.gamepadR?.buttons[4]?.pressed && this.gamepadButtonActive) {
      this.gamepadButtonActive = false;
    }
    if (this.gamepadR?.buttons[5]?.pressed && !this.gamepadButtonSecondaryActive && this.hostPose && this.inputs.hasPathFinderData) {
      this.gamepadButtonSecondaryActive = true;
    }
    if (!this.gamepadR?.buttons[5]?.pressed && this.gamepadButtonSecondaryActive) {
      this.gamepadButtonSecondaryActive = false;
      if (this.onControllerButtonSecondaryClicked) this.onControllerButtonSecondaryClicked();
    }
  }

  onTick() {
    if (this.context?.renderer?.xr?.isPresenting) {
      const _session = this.context.renderer.xr.getSession();
      _session.requestAnimationFrame(() => this.cycleVR(_session));
    }
  }
}

export const pathFinderMeshComponentType = 'mp.pathFinderMeshComponent';
export const makePathFinderMeshComponent = (): any => new PathFinderMeshComponent();
